import { useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import type { FormikProps } from "formik";
import * as L from "leaflet";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";

import { EmissionType, type HistoryState } from "src/types";
import { useEmission, useRoadTypes, useVehicleTypes } from "src/hooks";
import { useSegments } from "src/hooks/segments/useSegments";
import { useSegmentUpdateMutation } from "src/hooks/segments/useSegmentUpdateMutation";
import { useSegmentDeleteMutation } from "src/hooks/segments/useSegmentDeleteMutation";
import { useCase } from "src/hooks/useCase";
import { useScenario } from "src/hooks/useScenario";
// import { ConfirmUnsavedDialog } from "src/components/dialogs/ConfirmUnsavedDialog";
import { SegmentForm, type SegmentFormData } from "src/forms/SegmentForm";
import { isReadOnly } from "src/utils/permission.utils";
import { useTimefactors } from "src/hooks/timefactorHooks";
import { useBarrierHeights } from "src/hooks/barrierHooks";

interface Props {
  map: L.Map;
  onClose: (action?: HistoryState["action"]) => void;
}

export function TrafficEmissionSegmentEditPage(props: Props) {
  const { t } = useTranslation();
  const {
    caseId = "",
    scenarioId = "",
    emissionId = "",
    segmentId = "",
  } = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
    segmentId: string;
  }>();
  const confirm = useConfirm();
  const { data: acase } = useCase(caseId);
  const { data: scenario } = useScenario(scenarioId);
  const { data: roadTypes } = useRoadTypes();
  const { data: vehicleTypes } = useVehicleTypes(scenarioId);
  const { data: segments } = useSegments(scenarioId);
  const { data: emission } = useEmission(scenarioId, emissionId, EmissionType.TRAFFIC);
  const { data: timefactors } = useTimefactors();
  const { data: barrierHeights } = useBarrierHeights();

  const formRef = useRef<FormikProps<SegmentFormData> | null>(null);

  const updateMutation = useSegmentUpdateMutation(scenarioId, segmentId);
  const deleteMutation = useSegmentDeleteMutation(scenarioId, segmentId);

  const segment = useMemo(() => {
    if (!segments) return null;

    return segments.find((segment) => segment.segmentId.toString() === segmentId);
  }, [segmentId, segments]);

  /**
   * Effects
   */
  // ...

  /**
   * Functions
   */

  function handleDiscardChanges() {}

  function handleSave(values: SegmentFormData) {
    if (!segment) return Promise.reject();
    return updateMutation
      .mutateAsync({
        ...segment,
        ...values,
      })
      .then(() => props.onClose());
  }

  async function handleDelete() {
    if (!segment) return;

    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteSegment"),
    })
      .then(deleteMutation.mutateAsync)
      .then(() => props.onClose("rerenderWMS"));
  }

  return render();

  function render() {
    if (!acase || !scenario || !emission || !segment || !timefactors) return null;

    return (
      <>
        <SegmentForm
          key={segmentId}
          data={segment}
          timefactorType={acase.timefactorType}
          vehicleTypes={vehicleTypes || []}
          roadTypes={roadTypes || []}
          readOnly={isReadOnly(scenario.permissions.updateEmission)}
          isLoading={updateMutation.isLoading}
          timefactors={timefactors || []}
          barrierHeights={barrierHeights || []}
          isBaselineScenario={scenario.baseline}
          onSubmit={handleSave}
          onClose={() => {
            handleDiscardChanges();
            props.onClose();
          }}
          onDelete={handleDelete}
          errors={updateMutation.error?.response?.data}
          formRef={formRef}
        />
      </>
    );
  }
}
